import React, { useState, useMemo, useEffect } from 'react';
import { HiArrowRight, HiFunnel } from 'react-icons/hi2';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { conflictManager } from '../services/ConflictManager';

const weekKey = (week, year) => `${week ?? ''}-${year ?? ''}`;

const ConflictListModal = ({
    isOpen,
    onClose,
    conflicts,
    currentWeekNumber,
    currentYear,
    onNavigateToConflict
}) => {
    const [filterText, setFilterText] = useState('');
    const [showDismissed, setShowDismissed] = useState(false);
    const [dismissedVersion, setDismissedVersion] = useState(0);
    const [typeMenuOpen, setTypeMenuOpen] = useState(false);
    const [typeFilters, setTypeFilters] = useState({});

    useEffect(() => {
        conflictManager.cleanupDismissedConflicts();
        const unsubscribe = conflictManager.onDismissedChange(() => {
            setDismissedVersion(v => v + 1);
        });
        return () => unsubscribe();
    }, []);

    const getReasonType = (reasonText, reason = null) => {
        if (reason?.isCrossPeriod) return 'Cross-period';
        const lower = (reasonText || '').toLowerCase();
        if (lower.includes('holiday')) return 'Holiday';
        if (lower.includes('room') && lower.includes('occupied')) return 'Room occupied';
        if (lower.includes('room') && lower.includes('not suitable')) return 'Room not suitable';
        if (lower.includes('teacher') && lower.includes('unavailable')) return 'Teacher unavailable';
        if (lower.includes('teacher') && lower.includes('prefers away')) return 'Teacher prefers away';
        if (lower.includes('teacher') && lower.includes('conflicting class')) return 'Teacher conflict';
        if (lower.includes('group') && lower.includes('unavailable')) return 'Group unavailable';
        if (lower.includes('group') && lower.includes('prefers away')) return 'Group prefers away';
        if (lower.includes('group') && lower.includes('conflicting class')) return 'Group conflict';
        if (lower.includes('invalid input')) return 'Data issue';
        return 'Other';
    };

    const groupedConflicts = useMemo(() => {
        const groups = new Map();

        conflicts.forEach(conflict => {
            const key = `${conflict.itemId}-${conflict.day}-${conflict.time}-${conflict.room}`;

            if (!groups.has(key)) {
                groups.set(key, {
                    ...conflict,
                    reasonGroups: new Map()
                });
            }

            const group = groups.get(key);

            conflict.reasons.forEach(reason => {
                const baseMessage = reason.reason.replace(/, week \d+/i, '').replace(/\(week \d+\)/i, '').trim();
                const reasonId = conflictManager.makeConflictId(conflict.itemId, baseMessage);
                const weekMatch = reason.reason.match(/week (\d+)/i);
                const weekNumber = reason.week ?? (weekMatch ? parseInt(weekMatch[1], 10) : null);
                const year = reason.year ?? null;

                if (!group.reasonGroups.has(baseMessage)) {
                    group.reasonGroups.set(baseMessage, {
                        occurrences: new Map(),
                        severity: reason.severity,
                        dismissed: conflictManager.isConflictDismissed(reasonId),
                        reasonId,
                        isCrossPeriod: Boolean(reason.isCrossPeriod),
                        otherPeriodName: reason.otherPeriodName || null
                    });
                }

                const reasonGroup = group.reasonGroups.get(baseMessage);
                if (reason.isCrossPeriod) {
                    reasonGroup.isCrossPeriod = true;
                    if (reason.otherPeriodName) {
                        reasonGroup.otherPeriodName = reason.otherPeriodName;
                    }
                }
                if (reason.severity === 'red') {
                    reasonGroup.severity = 'red';
                }

                const occKey = weekKey(weekNumber, year);
                if (!reasonGroup.occurrences.has(occKey)) {
                    reasonGroup.occurrences.set(occKey, {
                        week: weekNumber,
                        year,
                        conflictingEntryIds: new Set()
                    });
                }
                if (reason.conflictingEntryId) {
                    reasonGroup.occurrences.get(occKey).conflictingEntryIds.add(reason.conflictingEntryId);
                }
            });
        });

        return Array.from(groups.values()).map(group => {
            const formattedReasons = Array.from(group.reasonGroups.entries()).map(([baseMessage, data]) => {
                const occurrences = Array.from(data.occurrences.values())
                    .filter(occ => occ.week != null)
                    .sort((a, b) => {
                        if (a.year != null && b.year != null && a.year !== b.year) return a.year - b.year;
                        return (a.week || 0) - (b.week || 0);
                    })
                    .map(occ => ({
                        week: occ.week,
                        year: occ.year,
                        conflictingEntryIds: Array.from(occ.conflictingEntryIds)
                    }));

                const weeks = occurrences.map(occ => occ.week);
                const weekText = weeks.length > 0 ? ` (weeks ${weeks.join(', ')})` : '';
                const reasonText = `${baseMessage}${weekText}`;
                return {
                    reason: reasonText,
                    baseMessage,
                    severity: data.severity,
                    dismissed: data.dismissed,
                    reasonId: data.reasonId,
                    type: getReasonType(reasonText, data),
                    isCrossPeriod: data.isCrossPeriod,
                    otherPeriodName: data.otherPeriodName,
                    occurrences
                };
            });

            return {
                ...group,
                reasons: formattedReasons,
                allWeeks: new Set(formattedReasons.flatMap(r => r.occurrences.map(o => o.week)))
            };
        }).filter(group => group.reasons.length > 0);
    }, [conflicts, dismissedVersion]);

    const normalizedFilter = filterText.trim().toLowerCase();

    const availableTypes = useMemo(() => {
        const types = new Set();
        groupedConflicts.forEach(conflict => {
            conflict.reasons.forEach(r => types.add(r.type || 'Other'));
        });
        return Array.from(types);
    }, [groupedConflicts]);

    const allSelected = useMemo(() => {
        if (availableTypes.length === 0) return false;
        return availableTypes.every(t => typeFilters[t] !== false);
    }, [availableTypes, typeFilters]);

    const anySelected = useMemo(() => {
        return availableTypes.some(t => typeFilters[t] !== false);
    }, [availableTypes, typeFilters]);

    useEffect(() => {
        setTypeFilters(prev => {
            const next = { ...prev };
            let changed = false;
            availableTypes.forEach(t => {
                if (next[t] === undefined) {
                    next[t] = true;
                    changed = true;
                }
            });
            Object.keys(next).forEach(t => {
                if (!availableTypes.includes(t)) {
                    delete next[t];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [availableTypes]);

    const filteredConflicts = useMemo(() => {
        if (!normalizedFilter) return groupedConflicts;
        return groupedConflicts.filter(conflict => {
            const subjectMatch = (conflict.itemName || '').toLowerCase().includes(normalizedFilter);
            const reasonMatch = (conflict.reasons || []).some(r =>
                (r.reason || '').toLowerCase().includes(normalizedFilter)
                || (r.type || '').toLowerCase().includes(normalizedFilter)
            );
            return subjectMatch || reasonMatch;
        });
    }, [groupedConflicts, normalizedFilter]);

    const visibleConflicts = useMemo(() => {
        return filteredConflicts
            .map(conflict => {
                const reasons = (showDismissed
                    ? conflict.reasons.filter(r => r.dismissed)
                    : conflict.reasons.filter(r => !r.dismissed))
                    .filter(r => typeFilters[r.type ?? 'Other'] !== false);
                return { ...conflict, reasons };
            })
            .filter(conflict => conflict.reasons.length > 0);
    }, [filteredConflicts, showDismissed, typeFilters]);

    const dismissedCount = useMemo(() => {
        return groupedConflicts.reduce((sum, conflict) => {
            return sum + conflict.reasons.filter(r => r.dismissed).length;
        }, 0);
    }, [groupedConflicts]);

    const activeCount = useMemo(() => {
        return groupedConflicts.reduce((sum, conflict) => {
            return sum + conflict.reasons.filter(r => !r.dismissed).length;
        }, 0);
    }, [groupedConflicts]);

    const pickOccurrence = (reason) => {
        const occurrences = reason.occurrences || [];
        if (occurrences.length === 0) return { week: currentWeekNumber, year: currentYear, conflictingEntryIds: [] };
        const current = occurrences.find(occ => occ.week === currentWeekNumber && (occ.year == null || occ.year === currentYear));
        return current || occurrences[0];
    };

    const navigateToReason = (conflict, reason, occurrence) => {
        if (!onNavigateToConflict) return;
        const target = occurrence || pickOccurrence(reason);
        const itemIds = [conflict.itemId, ...(target.conflictingEntryIds || [])].filter(Boolean);
        onNavigateToConflict({
            week: target.week,
            year: target.year ?? currentYear,
            itemIds,
            day: conflict.day
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={showDismissed ? `Dismissed conflicts (${visibleConflicts.length})` : `Schedule conflicts (${visibleConflicts.length})`}
        >
            <div className="flex flex-col h-[72vh]">
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            placeholder="Filter by subject or conflict text"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-gray-400"
                        />
                        <button
                            type="button"
                            className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700"
                            onClick={() => setTypeMenuOpen(v => !v)}
                            aria-label="Filter by conflict type"
                        >
                            <HiFunnel className="w-5 h-5" />
                        </button>
                        {typeMenuOpen && (
                            <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                                <div className="p-2 text-xs text-gray-500 font-semibold">Conflict types</div>
                                <div className="max-h-56 overflow-y-auto">
                                    {availableTypes.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-gray-500">No types available</div>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50 ${allSelected ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}
                                                onClick={() => {
                                                    const toggleTo = !allSelected;
                                                    const next = availableTypes.reduce((acc, t) => {
                                                        acc[t] = toggleTo;
                                                        return acc;
                                                    }, {});
                                                    setTypeFilters(next);
                                                }}
                                                aria-pressed={allSelected}
                                            >
                                                <span>All</span>
                                                <span className={`w-4 h-4 rounded border ${allSelected ? 'bg-blue-600 border-blue-600' : anySelected ? 'bg-blue-100 border-blue-400' : 'border-gray-300'}`}></span>
                                            </button>
                                            {availableTypes.map((type) => (
                                                <label key={type} className="flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50">
                                                    <span className="text-gray-700">{type}</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={typeFilters[type] !== false}
                                                        onChange={() =>
                                                            setTypeFilters(prev => ({
                                                                ...prev,
                                                                [type]: !(prev[type] !== false)
                                                            }))
                                                        }
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                </label>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button
                            variant={showDismissed ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setShowDismissed(v => !v)}
                            disabled={!dismissedCount && !showDismissed}
                            aria-label={showDismissed ? 'Show active conflicts' : 'Show dismissed conflicts only'}
                        >
                            {showDismissed ? 'Show active' : `Show dismissed${dismissedCount ? ` (${dismissedCount})` : ''}`}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                groupedConflicts.forEach(conflict => {
                                    conflict.reasons.forEach(r => {
                                        if (r.reasonId && !r.dismissed) {
                                            conflictManager.toggleConflictDismissed(r.reasonId);
                                        }
                                    });
                                });
                                setDismissedVersion(v => v + 1);
                            }}
                            disabled={activeCount === 0}
                            aria-label="Dismiss all conflicts"
                        >
                            Dismiss all
                        </Button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                    {visibleConflicts.length > 0 ? (
                        visibleConflicts.map((conflict, index) => (
                            <article
                                key={`${conflict.itemId}-${index}`}
                                className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                            >
                                <header className="mb-3">
                                    <h3 className="font-semibold text-gray-800 capitalize">
                                        {conflict.itemName}
                                    </h3>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        <span className="capitalize">{conflict.day}</span>
                                        {conflict.time ? ` · ${conflict.time}` : ''}
                                        {conflict.room ? ` · ${conflict.room}` : ''}
                                    </p>
                                </header>
                                <ul className="space-y-2">
                                    {conflict.reasons.map((reason) => {
                                        const isCurrentWeek = conflict.allWeeks.has(currentWeekNumber);
                                        const severityBorder = reason.severity === 'red' ? 'border-l-red-500' : 'border-l-orange-400';
                                        const severityText = reason.severity === 'red' ? 'text-red-700' : 'text-orange-700';

                                        return (
                                            <li
                                                key={reason.reasonId}
                                                className={`rounded-md border border-gray-200 border-l-4 ${severityBorder} bg-white p-3`}
                                            >
                                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                                    <div className="min-w-0 space-y-1.5">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className={`text-xs font-medium ${severityText}`}>
                                                                {reason.type}
                                                            </span>
                                                            {isCurrentWeek && (
                                                                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                                                    This week
                                                                </span>
                                                            )}
                                                            {reason.isCrossPeriod && reason.otherPeriodName && (
                                                                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">
                                                                    Other period · {reason.otherPeriodName}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className={`text-sm text-gray-700 ${reason.dismissed ? 'line-through text-gray-400' : ''}`}>
                                                            {reason.baseMessage || reason.reason}
                                                        </p>
                                                        {reason.occurrences.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 pt-0.5">
                                                                {reason.occurrences.map((occ) => {
                                                                    const isActiveWeek = occ.week === currentWeekNumber
                                                                        && (occ.year == null || occ.year === currentYear);
                                                                    return (
                                                                        <button
                                                                            key={weekKey(occ.week, occ.year)}
                                                                            type="button"
                                                                            onClick={() => navigateToReason(conflict, reason, occ)}
                                                                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                                                                isActiveWeek
                                                                                    ? 'bg-blue-600 text-white'
                                                                                    : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-700'
                                                                            }`}
                                                                            title="Show this week on the schedule"
                                                                        >
                                                                            {occ.year ? `${occ.year} · ` : ''}Week {occ.week}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-stretch">
                                                        {onNavigateToConflict && (
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => navigateToReason(conflict, reason)}
                                                                aria-label="Show conflicting entries on the schedule"
                                                                className="inline-flex items-center justify-center gap-1 whitespace-nowrap"
                                                            >
                                                                <HiArrowRight className="h-4 w-4" />
                                                                View
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                conflictManager.toggleConflictDismissed(reason.reasonId);
                                                                setDismissedVersion(v => v + 1);
                                                            }}
                                                            aria-label={reason.dismissed ? 'Restore conflict' : 'Dismiss conflict'}
                                                        >
                                                            {reason.dismissed ? 'Restore' : 'Dismiss'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </article>
                        ))
                    ) : (
                        <div className="text-center py-8">
                            {showDismissed ? (
                                <p className="text-gray-500">No dismissed conflicts.</p>
                            ) : normalizedFilter ? (
                                <p className="text-gray-500">No conflicts match that filter.</p>
                            ) : (
                                <p className="text-gray-500">No conflicts detected in the current schedule.</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="mt-6 flex justify-between items-center border-t border-gray-200 pt-4">
                    <p className="text-xs text-gray-500">
                        {showDismissed ? (
                            `Dismissed entries: ${visibleConflicts.length || 0}`
                        ) : (
                            `Entries with conflicts: ${visibleConflicts.length}${dismissedCount && !showDismissed ? ` · ${dismissedCount} dismissed hidden` : ''}`
                        )}
                    </p>
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ConflictListModal;
